/**
 * Catalogue Settings — banner (logo/text), color scheme, asset uploads, branding profiles.
 * Saves a LOCAL per-device branding override (sync-safe; master settings live in
 * Trac-IT and sync in as the base). Rest of app reads the merged result via useLiveSettings.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { db, saveSettingsOverride, clearSettingsOverride, hasSettingsOverride } from '../shared/services/db';
import { useLiveSettings } from '../hooks/useLiveSettings';
import { useAssetUrl } from '../hooks/useAssetUrl';
import { API_BASE_URL } from '../shared/services/apiClient';
import type { CatalogueSettings, CatalogueColours } from '../shared/types';

/** Brand colours in display order, with human labels and where each is used. */
const COLOUR_FIELDS: { key: keyof CatalogueColours; label: string; usage: string }[] = [
  { key: 'primary_dark', label: 'Primary dark', usage: 'PDF text & titles · Lookbook background · grid headers' },
  { key: 'primary_purple', label: 'Primary purple', usage: 'PDF category bands, allergens & price · buttons/chips' },
  { key: 'primary_pink', label: 'Primary pink', usage: 'PDF & Lookbook buttons / accents · Lookbook price' },
  { key: 'primary_gold', label: 'Primary gold', usage: 'Airline tier badge (PDF)' },
  { key: 'supplementary_light_yellow', label: 'Light yellow', usage: 'Reserved — not used yet' },
  { key: 'supplementary_light_pink', label: 'Light pink', usage: 'Reserved — not used yet' },
  { key: 'supplementary_lavender', label: 'Lavender', usage: 'PDF dividers (title & specs rules)' },
  { key: 'supplementary_grey', label: 'Grey', usage: 'PDF card borders & footer text' },
  { key: 'supplementary_light_grey', label: 'Light grey', usage: 'Lookbook photo-panel background' },
];

export const SettingsPage: React.FC = () => {
  const current = useLiveSettings();
  const [brandName, setBrandName] = useState(current.brand_name);
  const [brandSubtitle, setBrandSubtitle] = useState(current.brand_subtitle);
  const [logoFile, setLogoFile] = useState(current.logo_file);
  const [catalogueBannerFile, setCatalogueBannerFile] = useState(current.catalogue_banner_file);
  const [colours, setColours] = useState<CatalogueColours>(current.colours);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profiles, setProfiles] = useState<{ id: string; name: string; createdAt: number }[]>([]);
  const [personalized, setPersonalized] = useState(false);
  const [uploadedLogoName, setUploadedLogoName] = useState<string | null>(null);
  const [uploadedBannerName, setUploadedBannerName] = useState<string | null>(null);
  const [uploadedLookbookBannerName, setUploadedLookbookBannerName] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const lookbookBannerInputRef = useRef<HTMLInputElement>(null);

  const logoPreviewUrl = useAssetUrl(
    'logo',
    logoFile ? `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(logoFile)}` : null
  );
  const bannerPreviewUrl = useAssetUrl('banner', `${API_BASE_URL}/api/branding/catalogue-banner`);
  const lookbookBannerPreviewUrl = useAssetUrl('lookbook-banner', null);

  useEffect(() => {
    db.brandingProfiles.toArray().then(rows =>
      setProfiles(rows.map(r => ({ id: r.id, name: r.name, createdAt: r.createdAt })))
    );
    hasSettingsOverride().then(setPersonalized);
  }, []);

  useEffect(() => {
    setBrandName(current.brand_name);
    setBrandSubtitle(current.brand_subtitle);
    setLogoFile(current.logo_file);
    setCatalogueBannerFile(current.catalogue_banner_file);
    setColours(current.colours);
  }, [current.brand_name, current.brand_subtitle, current.logo_file, current.catalogue_banner_file, current.colours]);

  const loadUploadedFileNames = useCallback(() => {
    db.assetBlobs.get('logo').then(r => setUploadedLogoName(r?.fileName ?? null));
    db.assetBlobs.get('banner').then(r => setUploadedBannerName(r?.fileName ?? null));
    db.assetBlobs.get('lookbook-banner').then(r => setUploadedLookbookBannerName(r?.fileName ?? null));
  }, []);

  useEffect(() => {
    loadUploadedFileNames();
    const onAssetChanged = () => loadUploadedFileNames();
    window.addEventListener('catalogue-asset-changed', onAssetChanged);
    return () => window.removeEventListener('catalogue-asset-changed', onAssetChanged);
  }, [loadUploadedFileNames]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    db.assetBlobs.put({ id: 'logo', blob: file, fileName: file.name }).then(() => {
      setUploadedLogoName(file.name);
      window.dispatchEvent(new Event('catalogue-asset-changed'));
      setMessage({ type: 'success', text: 'Logo uploaded. It will appear in the header.' });
    });
    e.target.value = '';
  };

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    db.assetBlobs.put({ id: 'banner', blob: file, fileName: file.name }).then(() => {
      setUploadedBannerName(file.name);
      window.dispatchEvent(new Event('catalogue-asset-changed'));
      setMessage({ type: 'success', text: 'Banner uploaded. It will appear in the PDF.' });
    });
    e.target.value = '';
  };

  const handleLookbookBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    db.assetBlobs.put({ id: 'lookbook-banner', blob: file, fileName: file.name }).then(() => {
      setUploadedLookbookBannerName(file.name);
      window.dispatchEvent(new Event('catalogue-asset-changed'));
      setMessage({ type: 'success', text: 'Lookbook header banner uploaded.' });
    });
    e.target.value = '';
  };

  const handleSaveProfile = async () => {
    const name = window.prompt('Profile name (e.g. Didier, Délice)');
    if (!name?.trim()) return;
    const id = `profile-${Date.now()}`;
    const settings: CatalogueSettings = {
      brand_name: brandName,
      brand_subtitle: brandSubtitle,
      font_family: current.font_family,
      colours: colours,
      logo_file: logoFile,
      catalogue_banner_file: catalogueBannerFile,
      catalogue_banner_folder: current.catalogue_banner_folder,
      header_shapes_file: current.header_shapes_file,
      font_files: current.font_files,
    };
    await db.brandingProfiles.put({ id, name: name.trim(), createdAt: Date.now(), settings });
    setProfiles(prev => [...prev, { id, name: name.trim(), createdAt: Date.now() }]);
    setMessage({ type: 'success', text: `Profile "${name.trim()}" saved.` });
  };

  const handleLoadProfile = async (id: string) => {
    const row = await db.brandingProfiles.get(id);
    if (!row) return;
    setBrandName(row.settings.brand_name);
    setBrandSubtitle(row.settings.brand_subtitle);
    setLogoFile(row.settings.logo_file);
    setCatalogueBannerFile(row.settings.catalogue_banner_file);
    setColours(row.settings.colours);
    setMessage({ type: 'success', text: `Loaded profile "${row.name}". Click Save to apply to API.` });
  };

  const handleDeleteProfile = async (id: string) => {
    if (!window.confirm('Delete this profile?')) return;
    await db.brandingProfiles.delete(id);
    setProfiles(prev => prev.filter(p => p.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      // Personal branding override — saved LOCALLY (this device). The master
      // catalogue settings live in Trac-IT; they sync in as the base and this
      // override is layered on top, so it survives the nightly sync.
      const payload: Partial<CatalogueSettings> = {
        brand_name: brandName,
        brand_subtitle: brandSubtitle,
        logo_file: logoFile,
        catalogue_banner_file: catalogueBannerFile,
        colours: colours,
      };
      await saveSettingsOverride(payload);
      setPersonalized(true);
      window.dispatchEvent(new Event('catalogue-settings-changed'));
      setMessage({ type: 'success', text: 'Branding saved for this device. Header, colours and PDF will update.' });
    } catch (e: unknown) {
      const err = e as { message?: string };
      setMessage({ type: 'error', text: err.message ?? 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetSettings = async () => {
    if (!window.confirm('Reset branding to the catalogue defaults for this device?')) return;
    await clearSettingsOverride();
    setPersonalized(false);
    window.dispatchEvent(new Event('catalogue-settings-changed'));
    setMessage({ type: 'success', text: 'Branding reset to the catalogue settings.' });
  };

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2, color: '#363D4C' }}>
        Catalogue settings
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Brand name"
            value={brandName}
            onChange={e => setBrandName(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Brand subtitle"
            value={brandSubtitle}
            onChange={e => setBrandSubtitle(e.target.value)}
            fullWidth
            size="small"
          />
          <Typography variant="subtitle2" sx={{ color: '#555', mt: 1 }}>
            Logo
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            {logoPreviewUrl && (
              <Box
                component="img"
                src={logoPreviewUrl}
                alt="Logo preview"
                sx={{ width: 80, height: 48, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <TextField
              label="Logo file (API)"
              value={logoFile}
              onChange={e => setLogoFile(e.target.value)}
              size="small"
              placeholder="e.g. Didier_Grey.jpg"
              sx={{ flex: 1 }}
            />
            {uploadedLogoName && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Uploaded: {uploadedLogoName}
              </Typography>
            )}
            <input type="file" accept="image/*" ref={logoInputRef} onChange={handleLogoUpload} style={{ display: 'none' }} />
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => logoInputRef.current?.click()}>
              Upload
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ color: '#555', mt: 1 }}>
            Banner
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            {bannerPreviewUrl && (
              <Box
                component="img"
                src={bannerPreviewUrl}
                alt="Banner preview"
                sx={{ width: 120, height: 48, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <TextField
              label="Catalogue banner file (API)"
              value={catalogueBannerFile}
              onChange={e => setCatalogueBannerFile(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            {uploadedBannerName && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Uploaded: {uploadedBannerName}
              </Typography>
            )}
            <input type="file" accept="image/*" ref={bannerInputRef} onChange={handleBannerUpload} style={{ display: 'none' }} />
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => bannerInputRef.current?.click()}>
              Upload
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ color: '#555', mt: 1 }}>
            Lookbook Header Banner
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Full-width header image for the Lookbook (recommended 1920×200px). Falls back to dark header with logo.
          </Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            {lookbookBannerPreviewUrl && (
              <Box
                component="img"
                src={lookbookBannerPreviewUrl}
                alt="Lookbook banner preview"
                sx={{ width: 140, height: 40, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 1 }}
                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {uploadedLookbookBannerName && (
              <Typography variant="caption" color="text.secondary">
                {uploadedLookbookBannerName}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <input type="file" accept="image/*" ref={lookbookBannerInputRef} onChange={handleLookbookBannerUpload} style={{ display: 'none' }} />
            <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} onClick={() => lookbookBannerInputRef.current?.click()}>
              Upload
            </Button>
          </Stack>

          <Divider />

          <Typography variant="subtitle2" sx={{ color: '#555' }}>
            Branding profiles
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleSaveProfile}>
              Save current as profile
            </Button>
          </Stack>
          {profiles.length > 0 && (
            <List dense sx={{ bgcolor: '#f9f9f9', borderRadius: 1 }}>
              {profiles.map(p => (
                <ListItem key={p.id}>
                  <ListItemText primary={p.name} secondary={new Date(p.createdAt).toLocaleDateString()} />
                  <ListItemSecondaryAction>
                    <Button size="small" onClick={() => handleLoadProfile(p.id)} sx={{ mr: 0.5 }}>Load</Button>
                    <IconButton size="small" onClick={() => handleDeleteProfile(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Divider />
          <Typography variant="subtitle2" sx={{ color: '#555' }}>
            Colours
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Each colour drives specific parts of the PDF, Lookbook and catalogue grid — shown under each swatch.
          </Typography>
          {COLOUR_FIELDS.map(({ key, label, usage }) => {
            const value = colours[key] ?? '#000000';
            return (
              <Box key={key} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    component="label"
                    sx={{
                      width: 36,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: value,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      display: 'block',
                      flexShrink: 0,
                    }}
                  >
                    <input
                      type="color"
                      value={value}
                      onChange={e => setColours(prev => ({ ...prev, [key]: e.target.value }))}
                      style={{
                        opacity: 0,
                        width: '100%',
                        height: '100%',
                        cursor: 'pointer',
                        border: 'none',
                        padding: 0,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    {label}
                  </Typography>
                  <TextField
                    value={value}
                    onChange={e => setColours(prev => ({ ...prev, [key]: e.target.value }))}
                    size="small"
                    placeholder="#000000"
                    sx={{ width: 100, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: '46px', lineHeight: 1.3 }}>
                  {usage}
                </Typography>
              </Box>
            );
          })}
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: '#A55DA0', '&:hover': { bgcolor: '#8d4a88' } }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {personalized && (
              <Button variant="text" onClick={handleResetSettings} sx={{ color: '#A55DA0', textTransform: 'none' }}>
                Reset to catalogue defaults
              </Button>
            )}
          </Stack>
          {personalized && (
            <Typography variant="caption" color="text.secondary">
              Branding is personalized on this device.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
